import { Inject, Injectable } from '@angular/core';
import {
  Configuration,
  ConfigurationId,
} from '@daas-consulting/production/shared/cross/configuration';
import * as store from '@daas-consulting/security/authentication/frontend/store';
import { AuthenticationViewContract } from '@daas-consulting/security/authentication/frontend/view_contracts';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

// Extracted hardcoded colors to constants for maintainability
export const DEFAULT_VERSION_COLOR =
  'rgb(156 163 175 / var(--tw-text-opacity))' as const;
export const ACTIVE_VERSION_COLOR = 'white' as const;
export const DEFAULT_VERSION = '1.0.0' as const;
export const DAAS = 'daas' as const;
export const DEFAULT_LOGO = 'assets/logo.png' as const;
export const DEFAULT_COLOR = 'transparent' as const;
export const DEFAULT_LICENSE = 'MIT' as const;
export const DEFAULT_BORDER_WIDTH = '0' as const;
export const VAR_BORDER_COLOR = '--border-color' as const;
export const VAR_VERSION_COLOR = '--version-color' as const;
export const VAR_BORDER_WIDTH = '--border-width' as const;
export const DEFAULT_QR_CODE_APP_TITLE = '2FA' as const;

// Define an interface for the options used in setColors method
interface SetColorsOptions {
  borderColor: string;
  borderWidth: string;
}

interface AuthenticationConfiguration extends Configuration {
  version: string;
  name: string;
  loginRedirect: string;
  logoUrl: string;
  companyName: string;
  license: string;
  borderColor: string;
  borderWidth: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationViewModel implements AuthenticationViewContract {
  @Select(store.selectors.AuthenticationSelectors.accessToken)
  accessToken$!: Observable<string>;

  @Select(store.selectors.AuthenticationSelectors.userName)
  name$!: Observable<string>;

  @Select(store.selectors.AuthenticationSelectors.email)
  email$!: Observable<string>;

  @Select(store.selectors.AuthenticationSelectors.picture)
  picture$!: Observable<string>;

  @Select(store.selectors.AuthenticationSelectors.permissions)
  permissions$!: Observable<string[]>;

  @Select(store.selectors.AuthenticationSelectors.isAuthenticated)
  isAuthenticated$!: Observable<boolean>;

  @Select(store.selectors.AuthenticationSelectors.qrCode)
  qrCode$!: Observable<string>;

  readonly logoUrl$: Observable<string>;
  readonly companyName$: Observable<string>;
  readonly qrCodeAppTitle$: Observable<string>;
  readonly license$: Observable<string>;
  readonly version$: Observable<string>;
  readonly display$: Observable<string>;
  readonly borderColor$: Observable<string>;
  readonly borderWidth$: Observable<string>;
  readonly versionColor$: Observable<string>;
  readonly loginRedirect$: Observable<string>;

  readonly #logoUrl$ = new BehaviorSubject<string>('');
  readonly #companyName$ = new BehaviorSubject<string>(DAAS);
  readonly #qrCodeAppTitle$ = new BehaviorSubject<string>(
    DEFAULT_QR_CODE_APP_TITLE
  );
  readonly #license$ = new BehaviorSubject<string>(DEFAULT_LICENSE);
  readonly #version$ = new BehaviorSubject<string>(DEFAULT_VERSION);
  readonly #borderColor$ = new BehaviorSubject<string>(DEFAULT_COLOR);
  readonly #borderWidth$ = new BehaviorSubject<string>(DEFAULT_BORDER_WIDTH);
  readonly #versionColor$ = new BehaviorSubject<string>(DEFAULT_COLOR);
  readonly #loginRedirect$ = new BehaviorSubject<string>('');
  readonly #store: Store;
  readonly #configuration: Partial<AuthenticationConfiguration>;

  constructor(
    store: Store,
    @Inject(ConfigurationId) configuration: Partial<AuthenticationConfiguration>
  ) {
    this.#store = store;
    this.#configuration = configuration;
    this.logoUrl$ = this.#logoUrl$.asObservable();
    this.companyName$ = this.#companyName$.asObservable();
    this.qrCodeAppTitle$ = this.#qrCodeAppTitle$.asObservable();
    this.license$ = this.#license$.asObservable();
    this.version$ = this.#version$.asObservable();
    this.borderColor$ = this.#borderColor$.asObservable();
    this.borderWidth$ = this.#borderWidth$.asObservable();
    this.versionColor$ = this.#versionColor$.asObservable();
    this.loginRedirect$ = this.#loginRedirect$.asObservable();
    this.display$ = combineLatest([this.name$, this.email$]).pipe(
      map((data) => {
        const rv = data.filter(Boolean).join(' - ');
        return rv || '';
      })
    );

    const version = [
      this.#configuration?.version || DEFAULT_VERSION,
      this.#configuration?.name,
    ]
      .filter(Boolean)
      .join(' - ');
    this.#version$.next(version);
    this.#loginRedirect$.next(this.#configuration.loginRedirect ?? '');
    this.#logoUrl$.next(this.#configuration.logoUrl || DEFAULT_LOGO);
    this.#companyName$.next(this.#configuration.companyName || DAAS);
    this.#license$.next(this.#configuration.license || DEFAULT_LICENSE);
  }

  turnOn2fa(): void {
    const action = new store.actions.TurnOn();
    this.#store.dispatch(action);
  }

  login(code?: string): void {
    const action = new store.actions.Login(code);
    this.#store.dispatch(action);
  }

  logout(): void {
    const action = new store.actions.Logout();
    this.#store.dispatch(action);
  }

  generateQrCode(): void {
    const action = new store.actions.Generate();
    this.#store.dispatch(action);
  }

  gotoTurnOn(): void {
    const action = new store.actions.GotoTurnOn();
    this.#store.dispatch(action);
  }

  gotoGenerateQrCode(): void {
    const action = new store.actions.GotoGenerateQrCode();
    this.#store.dispatch(action);
  }

  validate2fa(): void {
    const action = new store.actions.Validate2fa();
    this.#store.dispatch(action);
  }

  setColors(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const environment = this.#configuration;
    this._setColors({
      borderColor: environment.borderColor,
      borderWidth: environment.borderWidth,
    });
  }

  private _setColors({
    borderColor = DEFAULT_COLOR,
    borderWidth = DEFAULT_BORDER_WIDTH,
  }: Partial<SetColorsOptions> = {}): void {
    const versionColor =
      borderColor === DEFAULT_COLOR
        ? DEFAULT_VERSION_COLOR
        : ACTIVE_VERSION_COLOR;
    document.documentElement.style.setProperty(VAR_BORDER_COLOR, borderColor);
    document.documentElement.style.setProperty(VAR_BORDER_WIDTH, borderWidth);
    document.documentElement.style.setProperty(VAR_VERSION_COLOR, versionColor);
    this.#borderColor$.next(borderColor);
    this.#borderWidth$.next(borderWidth);
    this.#versionColor$.next(versionColor);
  }
}
